// Event Alert Settings Manager
import React, { useState, useEffect } from 'react';
import { Loader2, Save, Bell, Calendar } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventAlertManager = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    days_before: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/event-alert-settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Error fetching event alert settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/event-alert-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setHasChanges(false);
      }
    } catch (err) {
      console.error('Error saving event alert settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-800">Alertes Événements</h3>
          <p className="text-sm text-slate-500">Afficher un bandeau sur la page d'accueil quand un événement approche</p>
        </div>
        <Button onClick={handleSave} disabled={saving || !hasChanges} size="sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Enable/Disable */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings.enabled ? 'bg-blue-100' : 'bg-slate-200'}`}>
              <Bell className={`w-5 h-5 ${settings.enabled ? 'text-blue-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h4 className="font-medium text-slate-800">Bandeau d'alerte</h4>
              <p className="text-sm text-slate-500">Afficher sur la page d'accueil</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enabled || false}
              onChange={() => handleChange('enabled', !settings.enabled)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
          </label>
        </div>
      </div>

      {/* Days before setting */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-200">
            <Calendar className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h4 className="font-medium text-slate-800">Jours avant l'événement</h4>
            <p className="text-sm text-slate-500">Commencer à alerter X jours avant</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="1"
            max="30"
            value={settings.days_before}
            onChange={(e) => handleChange('days_before', parseInt(e.target.value))}
            className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <div className="w-16 text-center">
            <span className="text-2xl font-bold text-slate-800">{settings.days_before}</span>
            <span className="text-xs text-slate-500 block">jours</span>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
          <span className="text-xs font-medium text-slate-500">APERÇU DES COULEURS</span>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded bg-red-600"></div>
            <span className="text-sm text-slate-600">Aujourd'hui / Demain</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded bg-orange-500"></div>
            <span className="text-sm text-slate-600">Dans 2-3 jours</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded bg-blue-600"></div>
            <span className="text-sm text-slate-600">Dans 4+ jours</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventAlertManager;
